/**
 * Personal-KB client (P4 我的资料 · P4a). A signed-in user's private library:
 * list / upload / delete. Mirrors the fetch + authHeaders idiom; upload sends
 * multipart FormData (the browser sets the boundary, so Content-Type is NOT set
 * explicitly). Routes 404 when FEATURE_PERSONAL_MATERIALS is off.
 */
import { authHeaders } from "./auth";

/** One file in the user's library (server manifest row). */
export interface PersonalFile {
  docId: string;
  filename: string;
  title: string;
  chunkCount: number;
  /** ISO timestamp of the last upload / re-sync (drives the "快照于 …" label). */
  uploadedAt: string;
  /** Ingest channel — "feishu" for a picked Feishu doc, else an upload. Drives the
   *  source badge and whether the row can be refreshed. Absent on legacy rows. */
  sourceKind?: string;
  /** Live doc link for a Feishu-sourced row. */
  sourceUrl?: string;
}

/** One node in the user's Feishu Drive (Stage 4b browse): folder or docx. */
export interface FeishuNode {
  token: string;
  name: string;
  type: string;
  url: string;
}

/** A failed upload with a user-facing reason derived from the HTTP status. */
export class PersonalKbError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "PersonalKbError";
  }
}

export const personalKbApi = {
  /** The caller's library, newest first. Empty on any failure. */
  list: async (): Promise<PersonalFile[]> => {
    const res = await fetch("/api/personal-kb", { headers: authHeaders() });
    if (!res.ok) return [];
    return ((await res.json()) as { files: PersonalFile[] }).files;
  },

  /** Upload one or many files in ONE request; returns the manifest rows +
   *  any filenames skipped server-side (unsupported types in a folder). */
  uploadMany: async (files: File[]): Promise<{ files: PersonalFile[]; skipped: string[] }> => {
    const form = new FormData();
    for (const f of files) form.append("file", f, f.name);
    const res = await fetch("/api/personal-kb/upload", {
      method: "POST",
      headers: authHeaders(),
      body: form,
    });
    if (!res.ok) {
      const reason =
        res.status === 415
          ? "这些文件类型暂不支持，先传文档（Word/PDF/Markdown/文本/网页）。"
          : res.status === 413
            ? "文件太大了，换个小一点的。"
            : "上传没成功，稍后再试。";
      throw new PersonalKbError(reason, res.status);
    }
    const body = (await res.json()) as { files?: PersonalFile[]; skipped?: string[] };
    return { files: body.files ?? [], skipped: body.skipped ?? [] };
  },

  /** Upload one file (convenience); returns its manifest row. */
  upload: async (file: File): Promise<PersonalFile> => {
    const { files } = await personalKbApi.uploadMany([file]);
    return files[0]!;
  },

  /** Stage 4b: browse a Feishu Drive folder (root when no token). */
  feishuBrowse: async (
    folder?: string,
    page?: string,
  ): Promise<{ files: FeishuNode[]; hasMore: boolean; nextPageToken?: string }> => {
    const qs = new URLSearchParams();
    if (folder !== undefined) qs.set("folder", folder);
    if (page !== undefined) qs.set("page", page);
    const res = await fetch(`/api/personal-kb/feishu/browse?${qs.toString()}`, { headers: authHeaders() });
    if (!res.ok) throw new PersonalKbError(res.status === 409 ? "飞书连接已失效，请重新连接。" : "打不开飞书文档。", res.status);
    return (await res.json()) as { files: FeishuNode[]; hasMore: boolean; nextPageToken?: string };
  },

  /** Stage 4b: add picked Feishu docs to 我的资料 (server reads + ingests). The
   *  url is carried through so provenance can link the answer back to the doc. */
  feishuPick: async (
    docs: { token: string; name: string; url?: string }[],
  ): Promise<{ files: PersonalFile[]; skipped: string[] }> => {
    const res = await fetch("/api/personal-kb/feishu/pick", {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ docs }),
    });
    if (!res.ok) throw new PersonalKbError("加入没成功，稍后再试。", res.status);
    const body = (await res.json()) as { files?: PersonalFile[]; skipped?: string[] };
    return { files: body.files ?? [], skipped: body.skipped ?? [] };
  },

  /** Stage 4b: re-sync picked Feishu docs from their live originals. Omit docIds
   *  to sync every Feishu-sourced row. 409 → the Feishu connection expired. */
  feishuRefresh: async (docIds?: string[]): Promise<{ files: PersonalFile[]; skipped: string[] }> => {
    const res = await fetch("/api/personal-kb/feishu/refresh", {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(docIds !== undefined ? { docIds } : {}),
    });
    if (!res.ok) {
      throw new PersonalKbError(
        res.status === 409 ? "飞书连接已失效，请在上方重新连接后再同步。" : "同步没成功，稍后再试。",
        res.status,
      );
    }
    const body = (await res.json()) as { files?: PersonalFile[]; skipped?: string[] };
    return { files: body.files ?? [], skipped: body.skipped ?? [] };
  },

  /** Remove one file from the library. */
  remove: async (docId: string): Promise<boolean> => {
    const res = await fetch(`/api/personal-kb/${encodeURIComponent(docId)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    return res.ok;
  },
};
